import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Checkbox,
} from '@mui/material'
import { useInvestmentContext } from '../contexts/InvestmentContext'
import { supabase } from '../supabaseClient'
import {
  investmentStatusLabel,
  investmentStatusColor,
  INVESTMENT_STATUS_LABELS,
  type InvestmentStatus,
} from '../constants/investmentStatus'
import type { InvestmentWithTenant } from '../types'

// Kolumny "czy w widgetcie X" — po slug widgetu (isbud).
const WIDGET_COLUMNS: { label: string; slug: string }[] = [
  { label: 'Na mapie', slug: 'isbud-map-widget' },
  { label: 'Aktualnie w sprzedaży', slug: 'isbud-magazine-widget' },
  { label: 'Aktualne inwestycje', slug: 'isbud-list-widget' },
  { label: 'Nasze realizacje', slug: 'isbud-archival-cards-widget' },
  { label: 'Wkrótce w sprzedaży', slug: 'isbud-minimal-widget' },
]

const STATUS_OPTIONS = Object.keys(INVESTMENT_STATUS_LABELS) as InvestmentStatus[]

type Membership = Record<string, Set<string>>

interface CustomCard {
  id: string
  name: string
  city: string
}

// Komórka widoczności — checkbox toggle obecności w widgecie. Tooltip = akcja po hover.
function WidgetToggleCell({
  present,
  label,
  onToggle,
}: {
  present: boolean
  label: string
  onToggle: () => void
}) {
  return (
    <TableCell align="center" padding="checkbox">
      <Tooltip title={present ? `Ukryj z: ${label}` : `Pokaż w: ${label}`}>
        <Checkbox size="small" checked={present} onChange={onToggle} />
      </Tooltip>
    </TableCell>
  )
}

export function InvestmentListView() {
  const { userInvestments, patchInvestment } = useInvestmentContext()
  const [tab, setTab] = useState(0)
  const [query, setQuery] = useState('')
  const [widgetIds, setWidgetIds] = useState<Record<string, string>>({})
  const [memberInv, setMemberInv] = useState<Membership>({})
  const [memberCustom, setMemberCustom] = useState<Membership>({})
  const [customCards, setCustomCards] = useState<CustomCard[]>([])
  const [statusEdit, setStatusEdit] = useState<{ inv: InvestmentWithTenant; value: InvestmentStatus } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    Promise.all([
      supabase.from('widgets').select('id, slug').in('slug', WIDGET_COLUMNS.map(c => c.slug)),
      supabase
        .from('widget_data_sources')
        .select('investment_id, custom_item_id, widgets!inner(slug)'),
      supabase.from('widget_data_custom_item').select('id, content'),
    ]).then(([widgetsRes, sourcesRes, customRes]) => {
      if (!active) return
      if (widgetsRes.data) {
        const ids: Record<string, string> = {}
        for (const w of widgetsRes.data as any[]) ids[w.slug] = w.id
        setWidgetIds(ids)
      }
      if (sourcesRes.data) {
        const inv: Membership = {}
        const cust: Membership = {}
        for (const row of sourcesRes.data as any[]) {
          const slug = row.widgets?.slug
          if (!slug) continue
          if (row.investment_id) (inv[slug] ??= new Set()).add(row.investment_id)
          if (row.custom_item_id) (cust[slug] ??= new Set()).add(row.custom_item_id)
        }
        setMemberInv(inv)
        setMemberCustom(cust)
      }
      if (customRes.data) {
        setCustomCards((customRes.data as any[]).map(r => ({
          id: r.id,
          name: r.content?.name ?? '—',
          city: r.content?.city ?? r.content?.filter_city ?? '—',
        })))
      }
    })
    return () => { active = false }
  }, [])

  const visibleInvestments = useMemo(() => {
    const q = query.trim().toLowerCase()
    return userInvestments
      .filter(inv => !q || inv.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
  }, [userInvestments, query])

  const visibleCards = useMemo(() => {
    const q = query.trim().toLowerCase()
    return customCards
      .filter(c => !q || c.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
  }, [customCards, query])

  async function toggleWidget(inv: InvestmentWithTenant, slug: string) {
    const widgetId = widgetIds[slug]
    if (!widgetId) return
    const present = memberInv[slug]?.has(inv.id) ?? false
    const { data, error } = await supabase.rpc('set_widget_investment', {
      p_widget_id: widgetId,
      p_investment_id: inv.id,
      p_is_present: !present,
    })
    if (error || !(data as any)?.success) return
    setMemberInv(prev => {
      const set = new Set(prev[slug] ?? [])
      if (present) set.delete(inv.id)
      else set.add(inv.id)
      return { ...prev, [slug]: set }
    })
  }

  async function toggleCustomWidget(cardId: string, slug: string) {
    const widgetId = widgetIds[slug]
    if (!widgetId) return
    const present = memberCustom[slug]?.has(cardId) ?? false
    const { data, error } = await supabase.rpc('set_widget_custom_item', {
      p_widget_id: widgetId,
      p_custom_item_id: cardId,
      p_is_present: !present,
    })
    if (error || !(data as any)?.success) return
    setMemberCustom(prev => {
      const set = new Set(prev[slug] ?? [])
      if (present) set.delete(cardId)
      else set.add(cardId)
      return { ...prev, [slug]: set }
    })
  }

  async function saveStatus() {
    if (!statusEdit) return
    setSaving(true)
    const { inv, value } = statusEdit
    const { data, error } = await supabase.rpc('update_investment_status', {
      p_investment_id: inv.id,
      p_status: value,
    })
    setSaving(false)
    if (error || !(data as any)?.success) return
    patchInvestment(inv.id, { status: value })
    setStatusEdit(null)
  }

  return (
    <Box p={2}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Inwestycje" />
        <Tab label="Dedykowane karty" />
      </Tabs>

      <TextField
        fullWidth
        size="small"
        placeholder="Szukaj elementu…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        sx={{ mb: 2, maxWidth: 480 }}
      />

      {tab === 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell>Status</TableCell>
              {WIDGET_COLUMNS.map(col => (
                <TableCell key={col.slug} align="center">{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleInvestments.map(inv => (
              <TableRow key={inv.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    {inv.name}
                    {inv.module_gov_reporting && (
                      <Tooltip title="Raportowanie do dane.gov.pl włączone">
                        <Chip label="Raportowanie" size="small" color="info" variant="outlined" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title="Zmień status">
                    <Chip
                      label={investmentStatusLabel(inv.status)}
                      size="small"
                      onClick={() =>
                        setStatusEdit({ inv, value: (inv.status ?? 'wkrotce') as InvestmentStatus })
                      }
                      sx={{
                        bgcolor: investmentStatusColor(inv.status).bg,
                        color: investmentStatusColor(inv.status).text,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                </TableCell>
                {WIDGET_COLUMNS.map(col => (
                  <WidgetToggleCell
                    key={col.slug}
                    label={col.label}
                    present={memberInv[col.slug]?.has(inv.id) ?? false}
                    onToggle={() => toggleWidget(inv, col.slug)}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell>Miasto</TableCell>
              {WIDGET_COLUMNS.map(col => (
                <TableCell key={col.slug} align="center">{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCards.map(card => (
              <TableRow key={card.id} hover>
                <TableCell>{card.name}</TableCell>
                <TableCell>{card.city}</TableCell>
                {WIDGET_COLUMNS.map(col => (
                  <WidgetToggleCell
                    key={col.slug}
                    label={col.label}
                    present={memberCustom[col.slug]?.has(card.id) ?? false}
                    onToggle={() => toggleCustomWidget(card.id, col.slug)}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {tab === 0 && visibleInvestments.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>Brak inwestycji</Typography>
      )}
      {tab === 1 && visibleCards.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>Brak kart</Typography>
      )}

      <Dialog open={!!statusEdit} onClose={() => setStatusEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle>Zmień status — {statusEdit?.inv.name}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Status"
            value={statusEdit?.value ?? ''}
            onChange={e =>
              setStatusEdit(prev => (prev ? { ...prev, value: e.target.value as InvestmentStatus } : prev))
            }
            sx={{ mt: 1 }}
          >
            {STATUS_OPTIONS.map(s => (
              <MenuItem key={s} value={s}>{INVESTMENT_STATUS_LABELS[s]}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusEdit(null)} disabled={saving}>Anuluj</Button>
          <Button onClick={saveStatus} variant="contained" disabled={saving}>Zapisz</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
